import { useRouter } from "next/router";
import dayjs from "dayjs";
import { useAtomValue } from "jotai";

import { useUserAgent } from "@/hooks/common/useUserAgent";
import ButtonV2 from "@/components/shared/buttonV2";
import Modal from "@/components/shared/modal";
import { userDataGlobalAtom } from "@/states/userDataGlobalAtom";

type Props = {
  errStep: string;
  error: any;
  showErrorModal: boolean;
  setShowErrorModal: React.Dispatch<React.SetStateAction<boolean>>;
  isReload?: boolean; //ネットワークエラー時にリロードするかどうか
};
type TitleProps = {
  children: React.ReactNode;
};

/* ファイルアップ時のエラー表示 */
const UploadErrorModal: React.FC<Props> = ({
  errStep,
  error,
  showErrorModal,
  setShowErrorModal,
  isReload
}) => {
  const router = useRouter();
  const { ua, browserName, browserVersion, mobileVendor, mobileModel } =
    useUserAgent();
  const userDetail = useAtomValue(userDataGlobalAtom);
  const isNetworkError = error?.message === "Network Error";

  //エラー内容のタイトル
  const Title: React.FC<TitleProps> = ({ children }) => {
    return <span className="mr-4 font-medium">{children}</span>;
  };

  return (
    <Modal
      show={showErrorModal}
      showClose={true}
      title="エラーが発生しました"
      titleRed={true}
      setShow={setShowErrorModal}
    >
      <div className="m-24 mt-0">
        <div className="text-md font-normal">
          {/* ネットワークエラーの場合 */}
          {isNetworkError ? (
            <div>
              <p>通信に失敗しました。</p>
              <p>
                通信環境の良い場所で画面を更新してから再度操作を行なってください。
              </p>
            </div>
          ) : (
            <div>
              <p>要求された処理に失敗しました。</p>
              <p>大変お手数ですが、画面を更新して再度お試しください。</p>
            </div>
          )}
          <p className="mt-4 text-sm">
            ※このエラーが解消しない場合は、スクリーンショットをCOSOJI事務局へ送付願います。
          </p>
        </div>

        {/* エラー内容 */}
        <div className="my-16 rounded-md bg-cBorderGray/50 px-16 py-8 text-md font-medium text-cErrorRed">
          <p>Step: {errStep}</p>
          <p>{error?.message}</p>
          <p>{error?.stack || ""}</p>
          <div className="mt-8 text-sm font-normal text-cCaptionGray">
            <ul className="ml-16 mt-4 list-outside list-disc gap-8">
              <li>
                <Title>userId:</Title>
                {userDetail?.userId}
              </li>
              <li>
                <Title>path:</Title>
                {router.asPath}
              </li>
              <li>
                <Title>DateTime:</Title>
                {dayjs().format("YYYY/MM/DD HH:mm:ss")}
              </li>
            </ul>
            {ua && (
              <ul className="ml-16 list-outside list-disc gap-8">
                <li>
                  <Title>{ua.mobile ? "Mobile" : "PC"}:</Title>
                  {`${ua.platform} (${ua.platformVersion})`}
                </li>
                <li>
                  <Title>Vendor:</Title>
                  {`${mobileVendor} (${mobileModel})`}
                </li>
                <li>
                  <Title>Browser:</Title>
                  {`${browserName} (${browserVersion})`}
                </li>
              </ul>
            )}
          </div>
        </div>

        <div className="mt-8 text-center">
          <ButtonV2
            variant="primary"
            layout="fullWidth"
            click={() => {
              setShowErrorModal(false);
              if (isReload && isNetworkError) {
                router.reload();
              }
            }}
          >
            閉じる{isReload && isNetworkError ? "（再読み込みをします）" : ""}
          </ButtonV2>
        </div>
      </div>
    </Modal>
  );
};

export default UploadErrorModal;
